import { createDraftSafeSelector } from '@reduxjs/toolkit';
import { auctionsBidsSelectors } from 'state/auctionsBids.slice';
import { internalSelectors } from 'state/internal.selectors';

const selectSelf = (state: RootState) => state.auctionPreview;
const bidsSelector = (state: RootState) => state.auctionsBids;
const selectAuctionsAttachments = (state: RootState) => state.attachments.rawAttachments;

const auctionFromStateSelector = createDraftSafeSelector(
  selectSelf,
  (auctionPreviewState) => auctionPreviewState.auction,
);

const auctionAttachmentsSelector = createDraftSafeSelector(
  selectSelf,
  selectAuctionsAttachments,
  (preview, attachments) => {
    const auctionNumber = Number(preview.auction?.auction_number);
    if (!attachments || !preview.auction || !attachments[auctionNumber]) {
      return {
        certificates: [],
        images: [],
        video360: [],
      };
    }
    return attachments[auctionNumber];
  },
);

const auctionsListsSelector = createDraftSafeSelector(
  (state: RootState) => state.auctions,
  (auctions) => ({
    suggested: auctions.suggested,
    viewed: auctions.viewed,
    trending: auctions.trending,
    promoted: auctions.promoted,
  }),
);

const auctionSelector = createDraftSafeSelector(
  auctionFromStateSelector,
  auctionsBidsSelectors.auctionBidsSelector,
  auctionsListsSelector,
  internalSelectors.auctionsWatchSelector,
  (auction, auctionBids, auctionsLists, watchedAuctionsList) => {
    if (!auction || !auction.auction_id) {
      return null;
    }

    const { suggested, viewed, trending, promoted } = auctionsLists;

    return {
      ...auctionBids,
      ...auction,
      watch: !!watchedAuctionsList[auction.auction_id],
      suggested: suggested.includes(auction.auction_id),
      viewed: viewed.includes(auction.auction_id),
      trending: trending.includes(auction.auction_id),
      promoted: promoted.includes(auction.auction_id),
    } as const;
  },
);

const adjacentAuctionSelector = (
  auctions: AuctionsTypes.AuctionType[] | MyAuctionsTypes.MyAuctionType[],
) =>
  createDraftSafeSelector(auctionSelector, (selected) => {
    if (auctions && selected) {
      const selectedIndex = auctions.findIndex(
        (auction) => auction.auction_number === selected.auction_number,
      );
      const next = auctions[selectedIndex + 1] || auctions[0];
      const prev = auctions[selectedIndex - 1] || auctions[auctions.length - 1];
      return { next, prev };
    }
    return {
      next: null,
      prev: null,
    };
  });

export const auctionPreviewSelectors = {
  auctionSelector,
  adjacentAuctionSelector,
  auctionAttachmentsSelector,
};
